/**
 * AnimatedThemeToggler Component
 *
 * An animated theme toggle button that uses the View Transitions API for a smooth
 * circular reveal animation when switching between light and dark modes.
 *
 * Integrates with next-themes for proper theme state management.
 *
 * @see https://magicui.design/docs/components/animated-theme-toggler
 */

'use client';

import { useCallback, useRef } from 'react';
import { Moon, Sun } from 'lucide-react';
import { flushSync } from 'react-dom';
import { useTheme } from 'next-themes';

import { cn } from '@/utils/cn';

// =============================================================================
// HOOK: useAnimatedThemeToggle
// =============================================================================

interface UseAnimatedThemeToggleOptions {
  /** Animation duration in milliseconds (default: 400ms) */
  duration?: number;
  /** Where the circular reveal animation originates from (default: 'center') */
  animationOrigin?: 'button' | 'center';
}

/**
 * Hook for animated theme toggling with View Transitions API.
 * Use this when you need to trigger the animated theme change from a custom component.
 *
 * @example
 * ```tsx
 * const { isDark, toggleTheme } = useAnimatedThemeToggle({ duration: 2400 });
 * <Switch checked={isDark} onCheckedChange={toggleTheme} />
 * ```
 */
export function useAnimatedThemeToggle(options: UseAnimatedThemeToggleOptions = {}) {
  const { duration = 400, animationOrigin = 'center' } = options;
  const { theme, resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const toggleTheme = useCallback(async () => {
    const newTheme = isDark ? 'light' : 'dark';

    // Check if View Transitions API is supported
    if (!document.startViewTransition) {
      setTheme(newTheme);
      return;
    }

    try {
      await document.startViewTransition(() => {
        flushSync(() => {
          setTheme(newTheme);
        });
      }).ready;

      const x = animationOrigin === 'center' ? window.innerWidth / 2 : 0;
      const y = animationOrigin === 'center' ? window.innerHeight / 2 : 0;

      const maxRadius = Math.hypot(
        Math.max(x, window.innerWidth - x),
        Math.max(y, window.innerHeight - y)
      );

      document.documentElement.animate(
        {
          clipPath: [
            `circle(0px at ${x}px ${y}px)`,
            `circle(${maxRadius}px at ${x}px ${y}px)`,
          ],
        },
        {
          duration,
          easing: 'ease-in-out',
          pseudoElement: '::view-transition-new(root)',
        }
      );
    } catch (error) {
      console.warn('[AnimatedThemeToggle] View transition failed, using fallback:', error);
      setTheme(newTheme);
    }
  }, [isDark, setTheme, duration, animationOrigin]);

  return { isDark, theme, toggleTheme };
}

// =============================================================================
// COMPONENT: AnimatedThemeToggler
// =============================================================================

interface AnimatedThemeTogglerProps
  extends React.ComponentPropsWithoutRef<'button'> {
  /** Animation duration in milliseconds (default: 400ms) */
  duration?: number;
  /** Where the circular reveal animation originates from (default: 'button') */
  animationOrigin?: 'button' | 'center';
}

export const AnimatedThemeToggler = ({
  className,
  duration = 400,
  animationOrigin = 'button',
  ...props
}: AnimatedThemeTogglerProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const toggleTheme = useCallback(async () => {
    if (!buttonRef.current) {
      // Fallback - just toggle theme without animation
      setTheme(isDark ? 'light' : 'dark');
      return;
    }

    // Check if View Transitions API is supported
    if (!document.startViewTransition) {
      // Fallback for browsers without View Transitions
      setTheme(isDark ? 'light' : 'dark');
      return;
    }

    try {
      await document.startViewTransition(() => {
        flushSync(() => {
          setTheme(isDark ? 'light' : 'dark');
        });
      }).ready;

      let x: number;
      let y: number;

      if (animationOrigin === 'center') {
        x = window.innerWidth / 2;
        y = window.innerHeight / 2;
      } else {
        const { top, left, width, height } =
          buttonRef.current.getBoundingClientRect();
        x = left + width / 2;
        y = top + height / 2;
      }

      const maxRadius = Math.hypot(
        Math.max(x, window.innerWidth - x),
        Math.max(y, window.innerHeight - y)
      );

      document.documentElement.animate(
        {
          clipPath: [
            `circle(0px at ${x}px ${y}px)`,
            `circle(${maxRadius}px at ${x}px ${y}px)`,
          ],
        },
        {
          duration,
          easing: 'ease-in-out',
          pseudoElement: '::view-transition-new(root)',
        }
      );
    } catch (error) {
      console.warn('[AnimatedThemeToggle] View transition failed, using fallback:', error);
      setTheme(isDark ? 'light' : 'dark');
    }
  }, [isDark, setTheme, duration, animationOrigin]);

  return (
    <button
      ref={buttonRef}
      onClick={toggleTheme}
      className={cn(className)}
      {...props}
    >
      {isDark ? (
        <Sun className="size-5 sm:size-4" strokeWidth={1.5} />
      ) : (
        <Moon className="size-5 sm:size-4" strokeWidth={1.5} />
      )}
      <span className="sr-only">
        {isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      </span>
    </button>
  );
};

AnimatedThemeToggler.displayName = 'AnimatedThemeToggler';
